import clsx from 'clsx';
import { FC, useState } from 'react';
import { ShowError } from '../../../errors/components/ShowError';
import { useUpdateShopTopicState } from '../api/telegram.mutations';
import {
	useGetCategories,
	useGetOutboxStats,
	useGetShopTopics,
} from '../api/telegram.queries';
import { ShopTopic, ShopTopicState } from '../model';

const STATE_LABEL: Record<ShopTopicState, string> = {
	active: 'Активен',
	muted: 'Приглушён',
	closed: 'Закрыт',
};

const STATE_BADGE: Record<ShopTopicState, string> = {
	active: 'badge-light-success',
	muted: 'badge-light-warning',
	closed: 'badge-light-danger',
};

const TopicRow: FC<{ topic: ShopTopic; categoryName: string }> = ({
	topic,
	categoryName,
}) => {
	const updateState = useUpdateShopTopicState();

	const handleState = async (state: ShopTopicState) => {
		if (
			state === 'closed' &&
			!window.confirm(
				`Закрыть топик магазина «${topic.shop_name}»?\n\n` +
					'Тема закроется и в Telegram, публикации прекратятся. ' +
					'Опубликованное останется видимым.'
			)
		) {
			return;
		}
		await updateState.mutateAsync({ topicUuid: topic.uuid, state });
	};

	return (
		<tr>
			<td>
				<div className='fw-bold'>{topic.shop_name}</div>
				<div className='text-muted fs-7'>{topic.bot_name ?? '—'}</div>
			</td>
			<td>{categoryName}</td>
			<td>
				<div>{topic.name}</div>
				<div className='text-muted fs-7'>
					<code>{topic.chat_id}</code> / {topic.message_thread_id}
				</div>
			</td>
			<td>
				<span className={clsx('badge', STATE_BADGE[topic.state])}>
					{STATE_LABEL[topic.state]}
				</span>
			</td>
			<td className='text-end'>
				<div className='d-flex gap-2 justify-content-end flex-wrap'>
					{topic.state === 'active' && (
						<button
							type='button'
							className='btn btn-sm btn-light-warning'
							disabled={updateState.isLoading}
							onClick={() => handleState('muted')}
						>
							Приглушить
						</button>
					)}
					{topic.state !== 'active' && (
						<button
							type='button'
							className='btn btn-sm btn-light-success'
							disabled={updateState.isLoading}
							onClick={() => handleState('active')}
						>
							Включить
						</button>
					)}
					{topic.state !== 'closed' && (
						<button
							type='button'
							className='btn btn-sm btn-light-danger'
							disabled={updateState.isLoading}
							onClick={() => handleState('closed')}
						>
							Закрыть
						</button>
					)}
				</div>
			</td>
		</tr>
	);
};

/** Топики магазинов: где живут и публикуются ли. Постит бот-витрина. */
export const ShopTopicsPage: FC = () => {
	const [categoryId, setCategoryId] = useState('');

	const { data, isLoading, error } = useGetShopTopics({
		categoryId: categoryId ? Number(categoryId) : undefined,
	});
	const { data: categoriesData } = useGetCategories();
	const { data: outbox } = useGetOutboxStats();

	const topics = data?.data ?? [];
	const categories = categoriesData?.categories ?? [];
	const categoryName = (id: number) =>
		categories.find(c => c.id === id)?.name ?? `#${id}`;

	if (error) return <ShowError error={error} />;

	return (
		<>
			<div className='card mb-5'>
				<div className='card-header'>
					<h3 className='card-title'>Очередь публикаций</h3>
				</div>
				<div className='card-body'>
					<div className='row g-5'>
						<div className='col-4'>
							<div className='text-muted fs-7'>В очереди</div>
							<div className='fw-bold fs-5'>{outbox?.pending ?? 0}</div>
						</div>
						<div className='col-4'>
							<div className='text-muted fs-7'>Опубликовано</div>
							<div className='fw-bold fs-5'>{outbox?.sent ?? 0}</div>
						</div>
						<div className='col-4'>
							<div className='text-muted fs-7'>Провалено</div>
							<div
								className={clsx(
									'fw-bold fs-5',
									(outbox?.failed ?? 0) > 0 ? 'text-danger' : ''
								)}
							>
								{outbox?.failed ?? 0}
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className='card'>
				<div className='card-header'>
					<h3 className='card-title'>Топики магазинов</h3>
					<div className='card-toolbar'>
						<select
							className='form-select form-select-sm w-200px'
							value={categoryId}
							onChange={e => setCategoryId(e.target.value)}
						>
							<option value=''>Все категории</option>
							{categories.map(category => (
								<option key={category.id} value={category.id}>
									{category.name}
								</option>
							))}
						</select>
					</div>
				</div>
				<div className='card-body'>
					{isLoading ? (
						<div className='text-muted'>Загрузка...</div>
					) : topics.length === 0 ? (
						<div className='text-muted'>Топиков нет</div>
					) : (
						<div className='table-responsive'>
							<table className='table table-row-bordered align-middle'>
								<thead>
									<tr className='fw-bold text-muted'>
										<th>Магазин</th>
										<th>Категория</th>
										<th>Топик</th>
										<th>Состояние</th>
										<th className='text-end'>Действия</th>
									</tr>
								</thead>
								<tbody>
									{topics.map(topic => (
										<TopicRow
											key={topic.uuid}
											topic={topic}
											categoryName={categoryName(topic.category_id)}
										/>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>
			</div>
		</>
	);
};
